import { addDays, format, setHours, setMinutes, subDays } from 'date-fns'
import { useState } from 'react'
import { useRemoteClientPost } from '../../../../configs/http-client'
import { Button } from '../../../components/ui/button'
import { DatePickerWithRange } from '../../../components/ui/date-range-picker'
import { Tabs, TabsList, TabsTrigger } from '../../../components/ui/tabs'
import { ResponsePayment } from './response-payments'

import * as XLSX from 'xlsx'
import { CatchMessageError } from '../../../../remote/catch-message-error'
import { useLocalStorage } from '../../../../remote/local-storage'
import { TableDefaulter } from '../../../components/table-defaulter'
import { formatToBRL } from '../../../utils/formatCurrency'
import { ResponseCashHistory } from './response-employees'
import { ResponseMembers } from './response-members'
import { ResponsePlans } from './response-plans'

export function Reports() {
  const { httpClientPost } = useRemoteClientPost('finance')
  const { httpClientPost: httpClientPostApi } = useRemoteClientPost()

  const [tab, setTab] = useState('Inadimplente')

  const { getUnitId } = useLocalStorage()

  const parseDateWithoutTimezone = (date: string, isFirst: boolean = true) => {
    const dateWithCorrectHours = setHours(date, isFirst ? 0 : 23)
    const dateWithCorrectHoursAndMinutes = setMinutes(dateWithCorrectHours, isFirst ? 0 : 59)
    return format(dateWithCorrectHoursAndMinutes, `yyyy-MM-dd HH:mm:ss`).replace(' ', 'T')
  }

  const [date, setDate] = useState({
    from: parseDateWithoutTimezone(new Date().toISOString()),
    to: parseDateWithoutTimezone(addDays(new Date(), 30).toISOString(), false)
  })

  const downloadPayments = async (status: 'PENDENTE' | 'RECEBIDO') => {
    try {
      const response = await httpClientPost('/pagamentos/filtro', {
        dataInicial: date.from,
        dataFinal: date.to,
        statusPagamento: status,
        idUnidade: getUnitId()
      })

      const payments: ResponsePayment[] = response.data.data.content
      const data = payments.map((p) => [p.cliente.nome, format(p.vencimento, 'dd/MM/yyyy'), formatToBRL(p?.valor.toString() || '0'), p.quantidadeParcelas, p.integracaoStatus, p.origem])

      const ws = XLSX.utils.aoa_to_sheet([['CLIENTE', 'VENCIMENTO', 'VALOR', 'QUANTIDADE PARCELAS', 'STATUS', 'ORIGEM'], ...data])
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Data')

      XLSX.writeFile(wb, `pagamentos-${status.toLowerCase()}-${format(new Date(), 'dd-MM-yyyy')}.xlsx`)
    } catch (error) {
      CatchMessageError(error)
    }
  }

  const downloadMembers = async (type: 'TODOS' | 'NOVOS') => {
    try {
      const dataInicial = parseDateWithoutTimezone(type == 'TODOS' ? date.from : subDays(new Date(), 15).toISOString())
      const dataFinal = parseDateWithoutTimezone(type == 'TODOS' ? date.to : new Date().toISOString())

      const response = await httpClientPostApi('/alunos/filtro', {
        dataInicial,
        dataFinal,
        unidadeId: getUnitId()
      })

      const responseMembers: ResponseMembers[] = response.data.data.content

      const data = responseMembers.map((item) => [item.nome, item.email, item.telefone.map((t) => `(${t?.ddd || ''}) ${t?.numero || ''}`).join(', '), item.unidade.nomeFantasia, item.cpf])

      const ws = XLSX.utils.aoa_to_sheet([['NOME', 'EMAIL', 'TELEFONE', 'UNIDADE', 'CPF'], ...data])
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Data')

      XLSX.writeFile(wb, `clientes-${format(new Date(), 'dd-MM-yyyy')}.xlsx`)
    } catch (error) {
      CatchMessageError(error)
    }
  }

  const downloadPlans = async () => {
    try {
      const response = await httpClientPostApi('/planos/filtro', {
        unidade: {
          id: getUnitId()
        }
      })

      const responsePlans: ResponsePlans[] = response.data.data.content

      const data = responsePlans.map((item) => [
        item.nome,
        item.descricao,
        item.quantidade,
        item.quantidadeUtilizada,
        format(item.dataVencimento, 'dd/MM/yyyy'),
        item.centroCusto.descricao,
        formatToBRL(item?.valor.toString() || '0')
      ])

      const ws = XLSX.utils.aoa_to_sheet([['NOME', 'DESCRIÇÃO', 'QUANTIDADE', 'QUANTIDADE UTILIZADA', 'VENCIMENTO', 'CENTRO DE CUSTO', 'VALOR'], ...data])
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Data')

      XLSX.writeFile(wb, `planos-${status.toLowerCase()}-${format(new Date(), 'dd-MM-yyyy')}.xlsx`)
    } catch (error) {
      CatchMessageError(error)
    }
  }

  const downloadCashHistory = async () => {
    try {
      const response = await httpClientPost('/caixa/fluxo-caixa', {
        dataInicial: date.from,
        dataFinal: date.to,
        idUnidade: getUnitId()
      })
      const responseCashHistory: ResponseCashHistory[] = response.data.data
      const data = responseCashHistory.map((item) => [item.periodo, formatToBRL(item.totalEntrada?.toString() || '0'), formatToBRL(item?.totalSaida.toString() || '0')])

      const ws = XLSX.utils.aoa_to_sheet([['MÊS/ANO', 'TOTAL ENTRADA', 'TOTAL SAIDA'], ...data])
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Data')

      XLSX.writeFile(wb, `caixa-${format(new Date(), 'dd-MM-yyyy')}.xlsx`)
    } catch (error) {
      console.error(error)
      CatchMessageError(error)
    }
  }

  return (
    <div className="">
      <div className="w-full overflow-x-auto">
        <Tabs defaultValue="Inadimplente" className="w-[320px]" value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="Inadimplente" className="data-[state=active]:bg-acelerafit-green-400 data-[state=active]:text-muted">
              Inadimplentes
            </TabsTrigger>

            <TabsTrigger value="Pagamentos" className="data-[state=active]:bg-acelerafit-green-400 data-[state=active]:text-muted">
              Pagamentos
            </TabsTrigger>

            <TabsTrigger value="Clientes" className="data-[state=active]:bg-acelerafit-green-400 data-[state=active]:text-muted">
              Clientes
            </TabsTrigger>

            <TabsTrigger value="Caixa" className="data-[state=active]:bg-acelerafit-green-400 data-[state=active]:text-muted">
              Caixa
            </TabsTrigger>

            <TabsTrigger value="Planos" className="data-[state=active]:bg-acelerafit-green-400 data-[state=active]:text-muted">
              Planos
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {tab != 'Inadimplente' && (
        <div className="flex flex-wrap justify-between min-h-10 mt-4 items-center gap-2 ">
          <h3 className="text-acelerait-black text-2xl font-bold">{tab}</h3>
          <DatePickerWithRange setInitDate={(value) => setDate({ ...date, from: value })} setFinishDate={(value) => setDate({ ...date, to: value })} />
        </div>
      )}

      {tab == 'Inadimplente' && <TableDefaulter />}

      {tab == 'Pagamentos' && (
        <div className="flex flex-row gap-4">
          <div className="rounded-md shadow-md p-6 flex flex-col gap-2">
            <h3 className="font-bold text-acelerait-black">Pagamentos Recebidos</h3>
            <Button className="text-muted bg-acelerafit-green-400 hover:bg-acelerafit-green-200 flex gap-2" onClick={() => downloadPayments('RECEBIDO')}>
              Baixar relatório
            </Button>
          </div>
          <div className="rounded-md shadow-md p-6 flex flex-col gap-2">
            <h3 className="font-bold text-acelerait-black">Pagamentos Pendentes</h3>
            <Button className="text-muted bg-acelerafit-green-400 hover:bg-acelerafit-green-200 flex gap-2" onClick={() => downloadPayments('PENDENTE')}>
              Baixar relatório
            </Button>
          </div>
        </div>
      )}

      {tab == 'Clientes' && (
        <div className="flex flex-row gap-4">
          <div className="rounded-md shadow-md p-6 flex flex-col gap-2">
            <h3 className="font-bold text-acelerait-black">Clientes</h3>
            <Button className="text-muted bg-acelerafit-green-400 hover:bg-acelerafit-green-200 flex gap-2" onClick={() => downloadMembers('TODOS')}>
              Baixar relatório
            </Button>
          </div>

          <div className="rounded-md shadow-md p-6 flex flex-col gap-2">
            <h3 className="font-bold text-acelerait-black">Novos Clientes</h3>
            <Button className="text-muted bg-acelerafit-green-400 hover:bg-acelerafit-green-200 flex gap-2" onClick={() => downloadMembers('NOVOS')}>
              Baixar relatório
            </Button>
          </div>
        </div>
      )}

      {tab == 'Caixa' && (
        <div className="flex flex-row gap-4">
          <div className="rounded-md shadow-md p-6 flex flex-col gap-2">
            <h3 className="font-bold text-acelerait-black">Caixa</h3>
            <Button className="text-muted bg-acelerafit-green-400 hover:bg-acelerafit-green-200 flex gap-2" onClick={downloadCashHistory}>
              Baixar relatório
            </Button>
          </div>
        </div>
      )}

      {tab == 'Planos' && (
        <div className="flex flex-row gap-4">
          <div className="rounded-md shadow-md p-6 flex flex-col gap-2">
            <h3 className="font-bold text-acelerait-black">Planos</h3>
            <Button className="text-muted bg-acelerafit-green-400 hover:bg-acelerafit-green-200 flex gap-2" onClick={downloadPlans}>
              Baixar relatório
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}
